import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@material-ui/core';

import Alert from '@material-ui/lab/Alert';

import moment from 'moment';

import PropTypes from 'prop-types';

import BasicButton from '../buttons/basicButton';
import CustomCheckBox from '../fields/customCheckBox';
import SpinnerLoading from '../feedback/spinnerLoading';

import { enrolPatientToProgram, getPrograms } from '../../utils/comms';

import translations from '../../languages/i18n';

import { customOptionsTableStyles } from '../../styles/formElementsStyles';

import { SessionContext } from '../../utils/context';

export default function AddEnrolmentsDialog({ enrolmentsData, handleClose, handleFeedback, idPatient, open, title }) {

  const sessionContext = useContext(SessionContext);
  const bgColors = customOptionsTableStyles();

  const [emptySelectionWarning, setEmptySelectionWarning] = useState(false);
  const [ready, setReady] = useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [programsList, setProgramsList] = useState([]);

  useEffect(() => {
    const getProgramsFromServer = async () => {
      setShowLoading(true);
      const response = await getPrograms(sessionContext.userToken); // Obtenim el llistat dels programes als que pertany el clínic. ALERTA: si l'usuari és l'Administrador obtenim tots els programes del sistema
      setShowLoading(false);
      if (response.status === 200) { // Tenim llistat
        let listOfPrograms = [];
        if (sessionContext.role === 'Admin') {
          // Si l'usuari és l'Administrador del sistema pot enrolar a tots els programes
          listOfPrograms = response.data.programs;
        } else {
          // Si l'usuari és un clínic administrador, només pot enrolar als programes que administra
          listOfPrograms = response.data.programs.filter((program) => {
            // Agafam els programes on el clínic és administrador
            return program.accessibleBy.clinicians.find((clinician) => clinician.clinician !== null && clinician.clinician._id === sessionContext.userId && clinician.admin === true);
          });
        }
        // Filtram la llista de programes de manera que només es mostrin els programes on encara no està enrolat el pacients
        const filteredListOfPrograms = listOfPrograms.filter((program) => {
          return !enrolmentsData.some((enrolment) => enrolment.program._id === program._id);
        });
        setProgramsList(filteredListOfPrograms);
        setReady(true);
      } else if (response === 400) { // Error amb les dades
        console.log('Error 400');
      } else { // Login KO. Altres errors
        console.log(response);
      }
    };
    getProgramsFromServer();
  }, [enrolmentsData]);

  const closeDialog = () => {
    handleClose(false); // Amb false indicam al handleClose que el pare NO ha de mostrar la nova llista de programes
  };

  const enrolPatient = async () => {
    if (selectedPrograms.length !== 0) { // Al manco ha seleccionat un programa del llistat
      setShowLoading(true);
      const responseEnrolPatientToProgram = await Promise.all(selectedPrograms.map((idProgram) => enrolPatientToProgram(
        idPatient,
        idProgram,
        sessionContext.userToken,
      )));
      setShowLoading(false);
      // Comprovam si dins les respostes n'hi ha alguna de timeOut o erronia
      const allOk = responseEnrolPatientToProgram.map((answer) => {
        if (answer.status && answer.status === 200) return 'ok';
        if (answer === 'timeOut') return 'timeOut';
        return 'error';
      });
      const idTO = allOk.indexOf('timeOut');
      const idE = allOk.indexOf('error');
      setSelectedPrograms([]); // Ha d'estar abans del HandleClose sinó intenta modificar l'estat d'un component desmontat
      if (idTO > -1 || idE > -1) { // Al manco hi ha 1 timeOut o 1 error
        // Miram si algun dels errors rebuts és un timeOut
        if (idTO > -1) { // Hi ha hagut un timeOut
          handleFeedback('error', 'TimeOut');
        } else { // Hi ha hagut un error
          handleFeedback('error', 'EnrolPatientToProgram');
        }
      } else { // No hi ha cap error
        handleClose(true); // Amb true indicam al handleClose que el pare ha de mostrar la nova llista de programes
        handleFeedback('success', 'EnrolPatientToProgram');
      }
    } else if (programsList.length !== 0) { // Si no ha seleccionat cap programa però el llistat no es buit, avisam a l'usuari
      setEmptySelectionWarning(true);
    } else {
      closeDialog();
    }
  };

  const handleCheck = (id) => {
    const selectedProgramsCopy = selectedPrograms.slice();
    // Cercam si dins selectedProgramsCopy hi ha el valor de program
    const idx = selectedProgramsCopy.findIndex((item) => item === id);
    if (idx < 0) selectedProgramsCopy.push(id); // Si idx és -1 vol dir que no ha trobat el valor, l'afegim a la taula
    else selectedProgramsCopy.splice(idx, 1); // L'ha trobat, l'eliminam de la taula
    setSelectedPrograms(selectedProgramsCopy);
    setEmptySelectionWarning(false);
  };

  const listOfPrograms = (programsData) => {
    return (
      <TableContainer component={Paper}>
        <Table aria-label='enrolments list table'>
          <TableBody>
            <TableRow className={bgColors.tableRowHeaderBgColor}>
              {/* ATENCIÓ: no pintam la columna _id */}
              <TableCell align='left'><strong>{translations['_name']}</strong></TableCell>
              <TableCell align='left'><strong>{translations['_status']}</strong></TableCell>
              <TableCell align='left'><strong>{translations['_type']}</strong></TableCell>
              <TableCell align='left'><strong>{translations['_versionDate']}</strong></TableCell>
              <TableCell align='left'></TableCell>
            </TableRow>
            {(programsData.length !== 0 && (
              programsData.map((data, index) => (
                <TableRow key={data._id} className={index % 2 ? bgColors.tableRowBgColor1 : bgColors.tableRowBgColor2} hover>
                  {/* ATENCIÓ: no pintam la columna _id */}
                  <TableCell align='left'>{translations[data.sharedId] || data.name}</TableCell>
                  <TableCell align='left'>{data.open ? translations['_opened'] : translations['_closed']}</TableCell>
                  <TableCell align='left'>{data.type}</TableCell>
                  <TableCell align='left'>{data.versionDate && moment(data.versionDate).format('DD-MM-YYYY')}</TableCell>
                  <TableCell align='center'>
                    <CustomCheckBox
                      checked={selectedPrograms.findIndex((item) => item === data._id) > -1} // Si res.value es dins la taula la condició dona true
                      onClick={() => handleCheck(data._id)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )) || (
              <TableRow>
                <TableCell align='center' colSpan='5'>
                  <Typography variant='h6' component='div'>{translations['_emptyProgramList']}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div>
      <Dialog
        open={open}
        // onClose={(handleClose2}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle id='alert-dialog-title'>{title}</DialogTitle>
        <DialogContent>
          <SpinnerLoading show={showLoading} />
          <Grid container spacing={2} justify='space-around' direction='column'>
            <Grid item xs={12}>
              {ready && listOfPrograms(programsList)}
            </Grid>
            {emptySelectionWarning && (
            <Grid item xs={12}>
              <Alert severity='error'>{translations['_emptyEnrolmentsSelection']}</Alert>
            </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <BasicButton handleClick={enrolPatient} type='accept' /> {/* Per enviar el formulari des d'un botó meu, sinó s'ha d'emprar un Button type='submit' */}
          <BasicButton handleClick={closeDialog} type='cancel' />
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Indica i controla els props que se han de passar al component
AddEnrolmentsDialog.propTypes = {
  enrolmentsData: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleFeedback: PropTypes.func.isRequired,
  idPatient: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
};
